import { TokenAttributesScopesEnum } from '@spazious/client-backoffice';
import { type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';

import { normalizeApiResponse } from '../_normalize.js';
import { invalidateMutationKey } from '../_utils.js';
import { useApiClient } from '../useApiClient.js';

export type SignInVariables = { username: string; password: string };

export type SignInResult = {
  access: string;
  refresh: string;
  scopes: string[] | null;
  role: number | null;
  userId: string | null;
  canImpersonate: boolean;
};

type NormalizedMe = {
  id?: string;
  profile?: {
    scopes?: string[];
    role?: number;
  };
};

type UseSignInParams = {
  mutationKey?: QueryKey | QueryKey[];
  onSuccess?: (data: SignInResult) => void;
  onError?: (error: unknown) => void;
};

export interface UseSignInResult {
  signIn: (variables: SignInVariables) => Promise<SignInResult>;
  isPending: boolean;
}

export const useSignIn = ({
  mutationKey,
  onSuccess,
  onError,
}: UseSignInParams = {}): UseSignInResult => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { mutateAsync: signIn, isPending } = useMutation({
    mutationFn: async ({ username, password }: SignInVariables): Promise<SignInResult> => {
      const loginResponse = await apiClient.users.createUsersAuthLogin({
        simpleJWTLoginRequest: { username, password },
      });

      const { access, refresh } = loginResponse.data;

      // The access token carries no scopes/role claim (stock SimpleJWT), so read
      // them from /me. The client instance still holds the previous auth header
      // (it is recreated only after `setAuth`), hence the explicit JWT header here.
      const meResponse = await apiClient.users.retrieveUsersMe(
        { include: 'profile' },
        {
          headers: {
            Authorization: `JWT ${access}`,
          },
        }
      );

      const me = normalizeApiResponse<NormalizedMe>(meResponse.data) as NormalizedMe;
      const scopes = me.profile?.scopes ?? null;
      const role = me.profile?.role ?? null;

      return {
        access,
        refresh,
        scopes,
        role,
        userId: me.id ?? null,
        // Capture the real user's impersonation capability now, before
        // QuickUserToggler overwrites scopes/role with the effective user.
        canImpersonate: scopes?.includes(TokenAttributesScopesEnum.Impersonateusers) ?? false,
      };
    },
    onSuccess: (data) => {
      onSuccess?.(data);
      invalidateMutationKey(queryClient, mutationKey);
    },
    onError,
  });

  return { signIn, isPending };
};
